/**
 * Local candidate review storage, merge, and export helpers.
 */

const REVIEWER_KEY_PREFIX = 'potamap-reviewer-';
const REVIEW_KEY_PREFIX = 'potamap-candidate-review-';

export const REVIEW_STATUSES = ['unreviewed', 'confirmed', 'rejected', 'needs_more_research'];

/**
 * @param {string} locId
 */
export function reviewStorageKey(locId) {
    return `${REVIEW_KEY_PREFIX}${locId}`;
}

/**
 * @param {string} locId
 */
export function reviewerStorageKey(locId) {
    return `${REVIEWER_KEY_PREFIX}${locId}`;
}

/**
 * @param {object|null|undefined} file
 * @returns {Map<string, object>}
 */
export function entriesFromReviewFile(file) {
    const map = new Map();
    if (!file) {
        return map;
    }

    const rows = file.entries && typeof file.entries === 'object'
        ? Object.entries(file.entries)
        : Array.isArray(file.entries)
            ? file.entries.map((row) => [row.candidateId, row])
            : [];

    for (const [candidateId, entry] of rows) {
        if (!candidateId || !entry) {
            continue;
        }
        map.set(candidateId, normalizeEntry(candidateId, entry));
    }

    return map;
}

/**
 * @param {string} candidateId
 * @param {object} entry
 */
export function normalizeEntry(candidateId, entry) {
    const normalized = {
        candidateId,
        reviewStatus: entry.reviewStatus || 'unreviewed',
        reviewer: entry.reviewer || null,
        date: entry.date || null,
        notes: entry.notes || '',
    };

    if (entry.setupPointOverride?.lat != null && entry.setupPointOverride?.lon != null) {
        normalized.setupPointOverride = {
            lat: Number(entry.setupPointOverride.lat),
            lon: Number(entry.setupPointOverride.lon),
        };
    }

    return normalized;
}

/**
 * @param {Map<string, object>} entries
 * @param {string} locId
 * @param {string|null} reviewer
 */
export function buildReviewFile(entries, locId, reviewer = null) {
    const payload = {
        locId,
        generatedAt: new Date().toISOString(),
        reviewer,
        entries: {},
    };

    for (const [candidateId, entry] of entries.entries()) {
        payload.entries[candidateId] = entry;
    }

    return payload;
}

/**
 * @param {object} candidate
 * @param {object|null|undefined} entry
 */
export function applyReviewToCandidate(candidate, entry) {
    const baseSetupPoint = candidate.originalSetupPoint || candidate.setupPoint || null;

    if (!entry) {
        return {
            ...candidate,
            reviewStatus: 'unreviewed',
            reviewNotes: '',
            effectiveSetupPoint: baseSetupPoint,
            setupPointIsOverride: false,
            setupPoint: baseSetupPoint,
        };
    }

    const effectiveSetupPoint = entry.setupPointOverride || baseSetupPoint || null;
    return {
        ...candidate,
        reviewStatus: entry.reviewStatus || 'unreviewed',
        reviewNotes: entry.notes || '',
        reviewer: entry.reviewer || null,
        reviewDate: entry.date || null,
        setupPointOverride: entry.setupPointOverride || null,
        effectiveSetupPoint,
        setupPointIsOverride: Boolean(entry.setupPointOverride),
        setupPoint: effectiveSetupPoint
            ? {
                ...(baseSetupPoint || {}),
                ...effectiveSetupPoint,
                method: entry.setupPointOverride ? 'manual_override' : baseSetupPoint?.method,
            }
            : baseSetupPoint,
    };
}

/**
 * @param {Map<string, object>} entries
 * @param {object} incoming
 */
export function mergeReviewEntry(entries, incoming) {
    const candidateId = incoming.candidateId;
    if (!candidateId) {
        return;
    }

    const existing = entries.get(candidateId);
    const merged = normalizeEntry(candidateId, {
        ...existing,
        ...incoming,
        setupPointOverride: incoming.setupPointOverride ?? existing?.setupPointOverride,
    });
    entries.set(candidateId, merged);
}

/**
 * @param {Map<string, object>} target
 * @param {Map<string, object>} incoming
 */
export function mergeReviewMaps(target, incoming) {
    for (const [candidateId, entry] of incoming.entries()) {
        mergeReviewEntry(target, entry);
    }
}

/**
 * @param {object[]} candidates
 * @param {Map<string, object>} entries
 */
export function splitCandidatesByReview(candidates, entries) {
    const confirmed = [];
    const rejected = [];

    for (const candidate of candidates) {
        const entry = entries.get(candidate.candidateId);
        const reviewed = applyReviewToCandidate(candidate, entry);
        if (reviewed.reviewStatus === 'confirmed') {
            confirmed.push(reviewed);
        } else if (reviewed.reviewStatus === 'rejected') {
            rejected.push(reviewed);
        }
    }

    return { confirmed, rejected };
}

/**
 * @param {object} payload
 * @param {string} filename
 */
export function downloadJson(payload, filename) {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
}

export class CandidateReviewStore {
    /**
     * @param {string} locId
     */
    constructor(locId) {
        this.locId = locId;
        this.entries = new Map();
        this.reviewer = null;
    }

    loadFromLocalStorage() {
        if (typeof localStorage === 'undefined') {
            return;
        }

        const reviewer = localStorage.getItem(reviewerStorageKey(this.locId));
        if (reviewer) {
            this.reviewer = reviewer;
        }

        const raw = localStorage.getItem(reviewStorageKey(this.locId));
        if (!raw) {
            return;
        }

        try {
            const parsed = JSON.parse(raw);
            mergeReviewMaps(this.entries, entriesFromReviewFile(parsed));
        } catch {
            // ignore corrupt local data
        }
    }

    saveToLocalStorage() {
        if (typeof localStorage === 'undefined') {
            return;
        }

        if (this.reviewer) {
            localStorage.setItem(reviewerStorageKey(this.locId), this.reviewer);
        }

        localStorage.setItem(
            reviewStorageKey(this.locId),
            JSON.stringify(buildReviewFile(this.entries, this.locId, this.reviewer)),
        );
    }

    /**
     * @param {string} url
     */
    async loadFromUrl(url) {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                return false;
            }
            const file = await response.json();
            if (file.reviewer) {
                this.reviewer = this.reviewer || file.reviewer;
            }
            mergeReviewMaps(this.entries, entriesFromReviewFile(file));
            return true;
        } catch {
            return false;
        }
    }

    /**
     * @param {object} file
     */
    importFile(file) {
        if (file.reviewer) {
            this.reviewer = file.reviewer;
        }
        mergeReviewMaps(this.entries, entriesFromReviewFile(file));
        this.saveToLocalStorage();
    }

    getReviewer() {
        return this.reviewer || 'reviewer';
    }

    setReviewer(name) {
        this.reviewer = name?.trim() || null;
        this.saveToLocalStorage();
    }

    getEntry(candidateId) {
        return this.entries.get(candidateId) || null;
    }

    /**
     * @param {string} candidateId
     * @param {object} patch
     */
    upsert(candidateId, patch) {
        mergeReviewEntry(this.entries, {
            candidateId,
            reviewer: this.getReviewer(),
            date: new Date().toISOString().slice(0, 10),
            ...this.entries.get(candidateId),
            ...patch,
        });
        this.saveToLocalStorage();
    }

    applyCandidate(candidate) {
        return applyReviewToCandidate(candidate, this.getEntry(candidate.candidateId));
    }

    exportReview() {
        downloadJson(
            buildReviewFile(this.entries, this.locId, this.reviewer),
            `candidate-review-${this.locId}.json`,
        );
    }

    /**
     * @param {object[]} candidates
     */
    exportConfirmed(candidates) {
        const { confirmed } = splitCandidatesByReview(candidates, this.entries);
        downloadJson({
            locId: this.locId,
            generatedAt: new Date().toISOString(),
            candidateCount: confirmed.length,
            candidates: confirmed,
        }, `confirmed-candidates-${this.locId}.json`);
    }

    /**
     * @param {object[]} candidates
     */
    exportRejected(candidates) {
        const { rejected } = splitCandidatesByReview(candidates, this.entries);
        downloadJson({
            locId: this.locId,
            generatedAt: new Date().toISOString(),
            candidateCount: rejected.length,
            candidates: rejected,
        }, `rejected-candidates-${this.locId}.json`);
    }
}
